import React, { useState, useContext, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  CircularProgress,
  useTheme,
  Alert,
  SelectChangeEvent,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import dayjs from 'dayjs';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';
import { exportToExcel } from '../../helpers/exportFuncs';

interface ProcessDetailsRecord {
  process_id: number;
  created: string;
  email_address: string;
  confirmed: string | null;
  opt_in_email_id: number;
  export_schema_id: number;
  user_address_created: string;
  user_agent_created: string | null;
  user_address_confirmed: string | null;
  user_agent_confirmed: string | null;
  clean_affiliate: string | null;
  origin_host: string | null;
  source: string | null;
  host: string | null;
  blog: string | null;
  isin: string | null;
}

interface FilterOptions {
  origin_hosts: string[];
  sources: string[];
  hosts: string[];
  blogs: string[];
  opt_in_emails: { id: number; name: string }[];
  export_schemas: number[];
}

interface ProcessDetailsFilters {
  email_address?: string;
  clean_affiliate?: string;
  origin_host?: string[];
  source?: string[];
  host?: string[];
  blog?: string[];
  isin?: string;
  opt_in_email_id?: number[];
  export_schema_id?: number[];
  confirmed?: 'yes' | 'no' | 'all';
  abTestGmail?: 'all' | '0' | '1' | 'unset';
}

const DoiProcessDetailsPage = () => {
  const { filterState } = useContext(FilterContext)!;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [filters, setFilters] = useState<ProcessDetailsFilters>({
    confirmed: 'all',
    abTestGmail: 'all',
  });
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    origin_hosts: [],
    sources: [],
    hosts: [],
    blogs: [],
    opt_in_emails: [],
    export_schemas: [],
  });
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState<ProcessDetailsRecord | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [soiCount, setSoiCount] = useState<number>(0);
  const [doiCount, setDoiCount] = useState<number>(0);

  const truncateUserAgent = (userAgent: string | null): string => {
    if (!userAgent || userAgent === 'null') return 'null';
    const maxLength = 50;
    if (userAgent.length <= maxLength) return userAgent;
    return userAgent.substring(0, maxLength) + '...';
  };

  const headerMapping: Record<string, HeaderMappingItem> = {
    process_id: { name: 'Process ID', type: 'string' }, // Changed to string to avoid number formatting
    created: { name: 'Created', type: 'datetime' },
    email_address: { name: 'Email', type: 'string' },
    clean_affiliate: { name: 'Affiliate', type: 'string' },
    confirmed: {
      name: 'Confirmed',
      type: 'custom',
      customRender: (value: string | null) => (
        <Box
          sx={{
            backgroundColor: value ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
            color: value ? 'success.dark' : 'error.dark',
            padding: '4px 8px',
            borderRadius: '4px',
            display: 'inline-block',
          }}
        >
          {value ? dayjs(value).format('DD.MM.YYYY, HH:mm') : 'Unconfirmed'}
        </Box>
      ),
    },
    opt_in_email_id: { name: 'Opt-in Email ID', type: 'number' },
    export_schema_id: { name: 'Export Schema ID', type: 'number' },
    user_address_created: { name: 'IP Created', type: 'string' },
    user_agent_created: {
      name: 'User Agent Created',
      type: 'custom',
      customRender: (value: string | null) => (
        <span title={value || 'null'}>{truncateUserAgent(value)}</span>
      ),
    },
    user_address_confirmed: { name: 'IP Confirmed', type: 'string' },
    user_agent_confirmed: {
      name: 'User Agent Confirmed',
      type: 'custom',
      customRender: (value: string | null) => (
        <span title={value || 'null'}>{truncateUserAgent(value)}</span>
      ),
    },
    origin_host: { name: 'Origin Host', type: 'string' },
    source: { name: 'Source', type: 'string' },
    host: { name: 'Host', type: 'string' },
    blog: { name: 'Blog', type: 'string' },
    isin: { name: 'ISIN', type: 'string' },
  };

  useEffect(() => {
    // Check if already authenticated in session storage
    const authenticated = sessionStorage.getItem('doi-process-details-auth');
    if (authenticated === 'true') {
      setIsAuthenticated(true);
      loadFilterOptions();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadFilterOptions();
    }
  }, [filterState, isAuthenticated]);

  const loadFilterOptions = async () => {
    const { startDate, endDate } = filterState;
    const startFormatted = dayjs(startDate).format('YYYY-MM-DD');
    const endFormatted = dayjs(endDate).format('YYYY-MM-DD');

    try {
      const options = await fetchData<FilterOptions>(
        `/api/doi-process-details/filter-options`,
        { startDate: startFormatted, endDate: endFormatted },
        false,
      );
      setFilterOptions(options as FilterOptions);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const fetchProcessDetails = useCallback(
    async (filterState: IFilterState): Promise<ProcessDetailsRecord[]> => {
      const { startDate, endDate } = filterState;
      const startFormatted = dayjs(startDate).format('YYYY-MM-DD');
      const endFormatted = dayjs(endDate).format('YYYY-MM-DD');

      const cleanFilters: ProcessDetailsFilters = {};
      Object.keys(filters).forEach((key) => {
        const value = filters[key as keyof ProcessDetailsFilters];
        if (
          value !== undefined &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0) &&
          value !== 'all'
        ) {
          cleanFilters[key as keyof ProcessDetailsFilters] = value as any;
        }
      });

      const filtersParam = JSON.stringify(cleanFilters);

      const response = (await fetchData<{
        data: ProcessDetailsRecord[];
        total: number;
        totalConfirmed: number;
      }>(
        `/api/doi-process-details/records`,
        {
          startDate: startFormatted,
          endDate: endFormatted,
          page: 0,
          limit: 500,
          filters: filtersParam,
        },
        false,
      )) as { data: ProcessDetailsRecord[]; total: number; totalConfirmed: number };

      // Set SOI and DOI counts
      setSoiCount(response.total);
      setDoiCount(response.totalConfirmed);

      return response.data;
    },
    [filters],
  );

  const exportAllData = async () => {
    setExporting(true);
    setExportProgress(0);

    const { startDate, endDate } = filterState;
    const startFormatted = dayjs(startDate).format('YYYY-MM-DD');
    const endFormatted = dayjs(endDate).format('YYYY-MM-DD');

    const cleanFilters: ProcessDetailsFilters = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key as keyof ProcessDetailsFilters];
      if (
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0) &&
        value !== 'all'
      ) {
        cleanFilters[key as keyof ProcessDetailsFilters] = value as any;
      }
    });

    const filtersParam = JSON.stringify(cleanFilters);

    try {
      const allData: ProcessDetailsRecord[] = [];
      let page = 0;
      const limit = 5000;

      const firstResponse = (await fetchData<{
        data: ProcessDetailsRecord[];
        total: number;
        totalConfirmed: number;
      }>(
        `/api/doi-process-details/records`,
        {
          startDate: startFormatted,
          endDate: endFormatted,
          page: page,
          limit: limit,
          filters: filtersParam,
        },
        false,
      )) as { data: ProcessDetailsRecord[]; total: number; totalConfirmed: number };

      allData.push(...firstResponse.data);
      const total = firstResponse.total;
      setExportProgress(Math.min(100, (allData.length / total) * 100));

      while (allData.length < total) {
        page++;
        const response = (await fetchData<{
          data: ProcessDetailsRecord[];
          total: number;
          totalConfirmed: number;
        }>(
          `/api/doi-process-details/records`,
          {
            startDate: startFormatted,
            endDate: endFormatted,
            page: page,
            limit: limit,
            filters: filtersParam,
          },
          false,
        )) as { data: ProcessDetailsRecord[]; total: number; totalConfirmed: number };
        allData.push(...response.data);
        setExportProgress(Math.min(100, (allData.length / total) * 100));
      }

      exportToExcel(allData, `doi-process-details-export-${startFormatted}-to-${endFormatted}`);
    } catch (error) {
      console.error('Error exporting all data:', error);
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const handleTextFilterChange =
    (field: keyof ProcessDetailsFilters) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }

      const timer = setTimeout(() => {
        setFilters((prev) => ({ ...prev, [field]: value }));
      }, 500);

      setSearchDebounceTimer(timer);
    };

  const handleMultiSelectChange =
    (field: keyof ProcessDetailsFilters) => (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value as string[];
      setFilters((prev) => ({ ...prev, [field]: value }));
    };

  const handleMultiSelectNumberChange =
    (field: keyof ProcessDetailsFilters) => (event: SelectChangeEvent<number[]>) => {
      const value = event.target.value as number[];
      setFilters((prev) => ({ ...prev, [field]: value }));
    };

  const handleConfirmedChange = (event: SelectChangeEvent) => {
    setFilters((prev) => ({ ...prev, confirmed: event.target.value as 'yes' | 'no' | 'all' }));
  };

  const handleAbTestGmailChange = (event: SelectChangeEvent) => {
    setFilters((prev) => ({
      ...prev,
      abTestGmail: event.target.value as 'all' | '0' | '1' | 'unset',
    }));
  };

  const clearFilters = () => {
    setFilters({ confirmed: 'all', abTestGmail: 'all' });
  };

  const getFilterCount = () => {
    let count = 0;
    Object.keys(filters).forEach((key) => {
      const value = filters[key as keyof ProcessDetailsFilters];
      if (value !== undefined && value !== '' && value !== 'all') {
        if (Array.isArray(value) && value.length > 0) {
          count++;
        } else if (!Array.isArray(value)) {
          count++;
        }
      }
    });
    return count;
  };

  const handleRowClick = (record: ProcessDetailsRecord) => {
    setSelectedRecord(record);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedRecord(null);
  };

  const formatDetailValue = (value: any): string => {
    if (value === null || value === undefined) return 'NULL';
    if (value === '') return '-';
    return String(value);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // if on localhost -> no password required
    if (window.location.hostname === 'localhost' || password === 'DOI-Quotevon1000%') {
      setIsAuthenticated(true);
      setPasswordError(false);
      sessionStorage.setItem('doi-process-details-auth', 'true');
    } else {
      setPasswordError(true);
    }
  };

  if (!isAuthenticated) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Paper
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            bgcolor: colors.primary[400],
          }}
        >
          <Typography variant="h5" gutterBottom align="center">
            DOI Process Details - Authentication Required
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }} align="center" color="text.secondary">
            This page requires additional authentication
          </Typography>
          <form onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              type="password"
              label="Password"
              variant="outlined"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError(false);
              }}
              error={passwordError}
              helperText={passwordError ? 'Incorrect password' : ''}
              sx={{ mb: 2 }}
              autoFocus
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              sx={{
                bgcolor: colors.greenAccent[600],
                '&:hover': {
                  bgcolor: colors.greenAccent[700],
                },
              }}
            >
              Access Page
            </Button>
          </form>
        </Paper>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" gutterBottom>
          DOI Process Details
        </Typography>
        <Box>
          {getFilterCount() > 0 && (
            <Chip
              label={`${getFilterCount()} Filter aktiv`}
              color="primary"
              onDelete={clearFilters}
              sx={{ mr: 2 }}
            />
          )}
          <Button
            variant="outlined"
            onClick={() => {
              setIsAuthenticated(false);
              sessionStorage.removeItem('doi-process-details-auth');
              setPassword('');
            }}
            sx={{ mr: 2 }}
          >
            Logout
          </Button>
          <Button
            variant="contained"
            onClick={exportAllData}
            disabled={exporting}
            sx={{ bgcolor: colors.greenAccent[600] }}
          >
            {exporting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Export ({Math.round(exportProgress)}%)
              </>
            ) : (
              'Export All'
            )}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3, bgcolor: colors.primary[400] }}>
        <Typography variant="h6" gutterBottom>
          Filter
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Email Address"
              variant="outlined"
              fullWidth
              size="small"
              onChange={handleTextFilterChange('email_address')}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Affiliate"
              variant="outlined"
              fullWidth
              size="small"
              onChange={handleTextFilterChange('clean_affiliate')}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="ISIN"
              variant="outlined"
              fullWidth
              size="small"
              onChange={handleTextFilterChange('isin')}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Confirmed</InputLabel>
              <Select
                value={filters.confirmed || 'all'}
                onChange={handleConfirmedChange}
                label="Confirmed"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>A/B Test Gmail</InputLabel>
              <Select
                value={filters.abTestGmail || 'all'}
                onChange={handleAbTestGmailChange}
                label="A/B Test Gmail"
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="0">Gmail Test: Default</MenuItem>
                <MenuItem value="1">Gmail Test: Workspace Email</MenuItem>
                <MenuItem value="unset">Ohne A/B Test</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Origin Host</InputLabel>
              <Select
                multiple
                value={filters.origin_host || []}
                onChange={handleMultiSelectChange('origin_host')}
                input={<OutlinedInput label="Origin Host" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {filterOptions.origin_hosts.map((host) => (
                  <MenuItem key={host} value={host}>
                    {host}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                multiple
                value={filters.source || []}
                onChange={handleMultiSelectChange('source')}
                input={<OutlinedInput label="Source" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {filterOptions.sources.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Host</InputLabel>
              <Select
                multiple
                value={filters.host || []}
                onChange={handleMultiSelectChange('host')}
                input={<OutlinedInput label="Host" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {filterOptions.hosts.map((host) => (
                  <MenuItem key={host} value={host}>
                    {host}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Blog</InputLabel>
              <Select
                multiple
                value={filters.blog || []}
                onChange={handleMultiSelectChange('blog')}
                input={<OutlinedInput label="Blog" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {filterOptions.blogs.map((blog) => (
                  <MenuItem key={blog} value={blog}>
                    {blog}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Opt-in Email</InputLabel>
              <Select
                multiple
                value={filters.opt_in_email_id || []}
                onChange={handleMultiSelectNumberChange('opt_in_email_id')}
                input={<OutlinedInput label="Opt-in Email" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map((value) => {
                      const email = filterOptions.opt_in_emails.find((e) => e.id === value);
                      return <Chip key={value} label={email?.name || value} size="small" />;
                    })}
                  </Box>
                )}
              >
                {filterOptions.opt_in_emails.map((email) => (
                  <MenuItem key={email.id} value={email.id}>
                    {email.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Export Schema ID</InputLabel>
              <Select
                multiple
                value={filters.export_schema_id || []}
                onChange={handleMultiSelectNumberChange('export_schema_id')}
                input={<OutlinedInput label="Export Schema ID" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {filterOptions.export_schemas.map((schema) => (
                  <MenuItem key={schema} value={schema}>
                    {schema}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="outlined" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      <TableWidget<ProcessDetailsRecord>
        key={`process-details-${JSON.stringify(filters)}`}
        getData={() => fetchProcessDetails(filterState)}
        detailsHeaderMapping={headerMapping}
        initialSortColumn="created"
        sortOrder="desc"
        searchByColumn="email_address"
        excelDownloadName={`doi-process-details-${dayjs(filterState.startDate).format('YYYY-MM-DD')}-to-${dayjs(filterState.endDate).format('YYYY-MM-DD')}`}
        onRowClick={handleRowClick}
      />

      <Paper sx={{ p: 2, mt: 2, bgcolor: colors.primary[400] }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                SOIs (Single Opt-Ins)
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: colors.blueAccent[500] }}>
                {soiCount.toLocaleString('de-DE')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total registrations
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                DOIs (Double Opt-Ins)
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: colors.greenAccent[500] }}>
                {doiCount.toLocaleString('de-DE')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Confirmed registrations
              </Typography>
              {soiCount > 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: colors.greenAccent[600] }}>
                  {((doiCount / soiCount) * 100).toFixed(1)}% DOI-Rate (unbereinigt)
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={detailsDialogOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, bgcolor: colors.primary[400] }}>
          <Typography variant="h6">Process Details - ID: {selectedRecord?.process_id}</Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseDetails}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRecord && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Process Information
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Process ID</TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.process_id)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Email Address</TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.email_address)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                      <TableCell>
                        {selectedRecord.created
                          ? dayjs(selectedRecord.created).format('DD.MM.YYYY, HH:mm:ss')
                          : 'NULL'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Confirmed</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            backgroundColor: selectedRecord.confirmed
                              ? 'rgba(76, 175, 80, 0.1)'
                              : 'rgba(244, 67, 54, 0.1)',
                            color: selectedRecord.confirmed ? 'success.dark' : 'error.dark',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            display: 'inline-block',
                          }}
                        >
                          {selectedRecord.confirmed
                            ? dayjs(selectedRecord.confirmed).format('DD.MM.YYYY, HH:mm:ss')
                            : 'Not Confirmed'}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Opt-in Email ID</TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.opt_in_email_id)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Export Schema ID</TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.export_schema_id)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                User Information - Creation
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                        IP Address (Created)
                      </TableCell>
                      <TableCell>
                        {formatDetailValue(selectedRecord.user_address_created)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>User Agent (Created)</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {formatDetailValue(selectedRecord.user_agent_created)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                User Information - Confirmation
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                        IP Address (Confirmed)
                      </TableCell>
                      <TableCell>
                        {formatDetailValue(selectedRecord.user_address_confirmed)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>User Agent (Confirmed)</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {formatDetailValue(selectedRecord.user_agent_confirmed)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Process Details Information
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                        Affiliate (Clean)
                      </TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.clean_affiliate)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Origin Host</TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.origin_host)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.source)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Host</TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.host)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Blog</TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.blog)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>ISIN</TableCell>
                      <TableCell>{formatDetailValue(selectedRecord.isin)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DoiProcessDetailsPage;
